import React, { MutableRefObject, useState } from "react";
import { CloseButton } from "@headlessui/react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { HiCheck, HiOutlineDocumentMagnifyingGlass } from "react-icons/hi2";

import Spinner from "@/components/atoms/Spinner";
import SearchInput from "@/components/molecules/SearchInput";

export type SelectAsyncComboBoxProps<T extends { id: string }> = {
  label?: string;
  options: T[] | undefined;
  displayValue?: (option: T) => string;
  placeholder?: string;
  renderOption?: (option: T, isSelected: boolean) => React.ReactNode;
  renderLoading?: React.ReactNode;
  hasError?: boolean;
  className?: string;
  optionsClassName?: string;
  disabled?: boolean;
  emptyNode?: React.ReactNode;
  isLoading?: boolean;
  onQueryUpdate?: (query: string) => void;
  selected?: string | undefined;
  onSelect?: (option: T) => void;
  observerRef?: MutableRefObject<HTMLDivElement | null>;
  containerRef?: MutableRefObject<HTMLDivElement | null>;
  isFetchingNextPage?: boolean;
  canSearch?: boolean;
  renderNextToLabel?: React.ReactNode;
};

const SelectAsyncComboBox = <T extends { id: string }>({
  label,
  options,
  displayValue = (option) => option?.id,
  placeholder,
  renderOption,
  renderLoading,
  hasError,
  className,
  optionsClassName,
  disabled,
  emptyNode,
  isLoading,
  onQueryUpdate,
  onSelect,
  selected,
  containerRef,
  observerRef,
  isFetchingNextPage,
  renderNextToLabel,
  canSearch = true,
}: SelectAsyncComboBoxProps<T>) => {
  const [isTyping, setIsTyping] = useState<boolean>(false);

  return (
    <div>
      {canSearch && (
        <SearchInput
          className={className}
          label={label}
          onQueryUpdate={onQueryUpdate}
          onTyping={setIsTyping}
          hasError={hasError}
          placeholder={placeholder}
          disabled={disabled}
        />
      )}

      {renderNextToLabel && <div>{renderNextToLabel}</div>}

      <div
        className={clsx("mt-3 max-w-full overflow-auto text-sm focus:outline-none", optionsClassName)}
        ref={containerRef}
      >
        {isLoading || isTyping || !options ? (
          renderLoading ? (
            renderLoading
          ) : (
            <IsLoading />
          )
        ) : options.length > 0 ? (
          options.map((option) => {
            if (onSelect) {
              const isSelected = option.id === selected || false;
              return (
                <CloseButton
                  key={option.id}
                  onClick={() => onSelect(option)}
                  className={clsx(
                    "flex cursor-pointer items-center rounded px-4 hover:bg-gray-100",
                    isSelected && "bg-secondary",
                  )}
                >
                  {renderOption ? renderOption(option, isSelected) : <span>{displayValue(option)}</span>}
                  {isSelected && (
                    <div className={"shrink-0 px-2"}>
                      <HiCheck className="size-6 shrink-0 fill-fg-brand-primary" />
                    </div>
                  )}
                </CloseButton>
              );
            }

            return renderOption ? renderOption(option, false) : <span>{displayValue(option)}</span>;
          })
        ) : (
          emptyNode || <NoResults />
        )}

        {isFetchingNextPage ? renderLoading ? renderLoading : <IsLoading /> : null}

        <div ref={observerRef} />
      </div>
    </div>
  );
};

export default SelectAsyncComboBox;

const NoResults = () => {
  const { t } = useTranslation();

  return (
    <div className={"flex flex-col items-center justify-center px-3 py-4 text-center text-sm text-quaternary"}>
      <HiOutlineDocumentMagnifyingGlass className={"mb-2 size-8"} />
      <p>{t("noResults.found")}</p>
      <p>{t("noResults.try")}</p>
    </div>
  );
};

const IsLoading = () => {
  const { t } = useTranslation();
  return (
    <div className={"flex items-center gap-2 px-3 py-4 text-sm text-quaternary"}>
      <Spinner />
      <p>{t("searching")}</p>
    </div>
  );
};
